'use client'

import { useState } from 'react'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { Button } from '@achieve/ascend'
import { Minus, Plus } from 'react-feather'
import { addUrlParameter } from 'utils/shared'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { useTriggerEvents } from 'hooks/useTriggerEvents'

const createImageContent = (src) => ({
  fields: {
    file: {
      url: src,
      fileName: 'hero-json-borrow-box-image',
    },
  },
})

const dollarUSLocale = Intl.NumberFormat('en-US')

function LayerImage({ src, alt, decorative, priority, width, height, wrapperClassName, wrapperStyle, imageClassName = 'w-full h-auto' }) {
  if (!src) return null

  const imgProps = decorative ? { alt: '', 'aria-hidden': true } : { alt }

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      <MediaImageStandard
        content={createImageContent(src)}
        width={width}
        height={height}
        className={imageClassName}
        priority={priority}
        {...imgProps}
      />
    </div>
  )
}

function BorrowBox({
  personImage,
  chartImage,
  boxBackgroundImage,
  borrowBox = {},
  sectionVariationObject,
}) {
  const {
    title,
    defaultValue = 20000,
    minValue = 5000,
    maxValue = 50000,
    step = 1000,
    maxPlus = false,
    subText,
    buttonText,
    buttonLink,
  } = borrowBox

  const min = Number(minValue)
  const max = Number(maxValue)
  const stepValue = Number(step)
  const clamp = (val) => Math.min(max, Math.max(min, val))

  const [value, setValue] = useState(clamp(Number(defaultValue)))
  const { fireAndForget } = useTriggerEvents()

  const updateValue = (event, direction) => {
    const nextValue = clamp(value + direction * stepValue)
    const clickText = direction > 0 ? 'Increase amount' : 'Decrease amount'

    setValue(nextValue)
    fireAndForget({
      event_type: 'button_click',
      ...handleTrackAndReactEvent(event, {
        list_name: 'Hero Form',
        click_id: clickText,
        click_text: clickText !== 'Increase amount' ? '-' : '+',
        form_ss_amount: nextValue,
        nav_link_section: 'Hero Form',
        track_event: 'button_click',
        event_action: 'button_click',
        event_type: 'button_click',
        sectionVariationObject,
      }),
    })
  }

  const decrement = (event) => updateValue(event, -1)
  const increment = (event) => updateValue(event, 1)

  const isMin = value <= min
  const isMax = value >= max
  const displayValue = `$${dollarUSLocale.format(value)}${isMax && maxPlus ? '+' : ''}`
  const resolvedButtonLink = buttonLink
    ? addUrlParameter(buttonLink, 'loan_amount', value)
    : undefined

  const stepperButtonClassName =
    'flex box-border h-[30px] w-[30px] p-0 shrink-0 items-center justify-center rounded-full border-[3px] border-solid border-[#DADEE5] bg-white text-[#42546B] cursor-pointer transition-opacity disabled:opacity-40 disabled:cursor-default'

  return (
    <div className="relative isolate z-0 w-full min-w-0 max-w-[640px] mx-auto breakpointLg:min-h-[420px] breakpointLg:h-[500px] breakpointLg:max-h-[500px]">
      <LayerImage
        src={boxBackgroundImage}
        decorative
        width={640}
        height={520}
        wrapperClassName="hidden breakpointLg:flex absolute inset-0 -z-10 items-end justify-center pointer-events-none"
        imageClassName="w-full h-auto breakpointLg:h-full breakpointLg:object-contain breakpointLg:object-right-bottom"
      />
      <LayerImage
        src={personImage}
        alt="Hero image"
        priority
        width={640}
        height={520}
        wrapperClassName="hidden breakpointLg:flex relative z-0 justify-end breakpointLg:absolute breakpointLg:inset-0"
        imageClassName="w-full h-auto breakpointLg:h-full breakpointLg:object-contain breakpointLg:object-right-bottom"
      />
      <div className="relative z-20 w-full flex justify-center breakpointLg:absolute breakpointLg:left-0 breakpointLg:bottom-[150px] breakpointLg:justify-start">
        <div className="relative w-full max-w-[520px] min-w-[300px] breakpointLg:w-[365px]">
          <LayerImage
            src={chartImage}
            decorative
            width={260}
            height={220}
            wrapperClassName="hidden breakpointLg:block absolute z-10 bottom-full left-1/2 w-[40%] max-w-[260px] pointer-events-none"
            wrapperStyle={{ transform: 'translate(-30%, calc(42px - 10%))' }}
          />
          <div className="relative z-20 shadow-sm bg-white py-6 px-8 border border-solid border-blue-100 rounded-2xl breakpointMd:py-8 breakpointLg:box-border breakpointLg:w-[365px] breakpointLg:min-h-[177px] breakpointLg:px-[32px]">
          {title && (
            <Typography
              content={title}
              variant="bodyMd"
              className="font-ultramarine text-[16px]! font-normal! not-italic leading-[140%]! text-[color:var(--content-informational,#42546B)] text-center mb-4 breakpointMd:mb-[18px]"
            />
          )}
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              aria-label="Decrease amount"
              onClick={decrement}
              disabled={isMin}
              className={stepperButtonClassName}
            >
              <Minus size={20} strokeWidth={3} color="#DADEE5" />
            </button>
            <Typography
              content={displayValue}
              variant="headingLg"
              fontWeight="bold"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="font-ultramarine text-[24px]! breakpointMd:text-[32px]! font-bold! not-italic leading-[130%]! text-[color:var(--content-primary,#1D252F)] text-center"
              data-testid="borrow-box-amount"
            />
            <button
              type="button"
              aria-label="Increase amount"
              onClick={increment}
              disabled={isMax}
              className={stepperButtonClassName}
              style={{ borderColor: '#42546B' }}
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </div>
          <div className="flex items-center justify-between mt-3 font-ultramarine text-[14px] font-normal not-italic leading-[140%] text-[color:var(--content-informational,#42546B)]">
            <span>{`$${dollarUSLocale.format(min)}`}</span>
            <span className="text-right">{`$${dollarUSLocale.format(max)}${maxPlus ? '+' : ''}`}</span>
          </div>
          {buttonText && resolvedButtonLink && (
            <AchieveLink
              href={resolvedButtonLink}
              queryParamsToPreserve={['loan_amount']}
              track={{
                list_name: 'Hero Form',
                click_id: buttonText,
                click_text: buttonText,
                click_url: resolvedButtonLink,
                hero_card_text: buttonText,
                form_ss_amount: value,
                nav_link_section: 'Hero Form',
                track_event: 'button_click',
                event_action: 'button_click',
                event_type: 'button_click',
                sectionVariationObject,
              }}
            >
              <Button color="primary" variant="contained" className="w-full mt-4 breakpointMd:mt-[18px] breakpointLg:h-[36px] breakpointLg:min-h-0 breakpointLg:py-0">
                {buttonText}
              </Button>
            </AchieveLink>
          )}
          {subText && (
            <Typography
              content={subText}
              variant="bodyXs"
              className="mt-3 text-center text-[12px] text-[#5C708A]"
            />
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BorrowBox
